class NRRDParser {
  static parseNRRD(text) {
    // const lines = text.split(/\r?\n/);
    const lines = text.split(/\n/);

    const header = {};
    let dataOffset = 0;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      if (line === "") {
        dataOffset = i + 1;
        break;
      }

      if (line.startsWith("#")) continue;

      const colonIndex = line.indexOf(":");
      if (colonIndex === -1) continue;

      const key = line.substring(0, colonIndex).trim();
      const value = line.substring(colonIndex + 1).trim();

      header[key] = value;
    }

    const data = lines.slice(dataOffset).join("\n");

    return {
      header,
      data,
    };
  }

  static decodeData(header, binaryData) {
    const encoding = header.encoding || "raw";
    let dataToParse = binaryData;

    if (encoding === "gzip" || encoding === "gz") {
      try {
        dataToParse = pako.inflate(binaryData);
      } catch (e) {
        console.error("Error decompressing gzip data:", e);
        throw e;
      }
    } else if (encoding === "ascii") {
      const text = new TextDecoder().decode(binaryData);
      const numbers = text.trim().split(/\s+/).map(parseFloat);
      return {
        typedArray: new Float32Array(numbers),
        sizes: header.sizes ? header.sizes.split(" ").map(Number) : null,
        bytesPerElement: 4,
      };
    } else if (encoding !== "raw") {
      throw new Error(`Unsupported encoding: ${encoding}`);
    }

    return this.parseTypedArray(header, dataToParse);
  }

  static parseTypedArray(header, binaryData) {
    const type = header.type || "float";
    const endian = header.endian || "little";

    let typedArray;
    let bytesPerElement;

    switch (type) {
      case "float":
        typedArray = new Float32Array(binaryData.buffer);
        bytesPerElement = 4;
        break;
      case "double":
        typedArray = new Float64Array(binaryData.buffer);
        bytesPerElement = 8;
        break;
      case "short":
      case "int16":
        typedArray = new Int16Array(binaryData.buffer);
        bytesPerElement = 2;
        break;
      case "ushort":
      case "uint16":
        typedArray = new Uint16Array(binaryData.buffer);
        bytesPerElement = 2;
        break;
      case "int":
      case "int32":
        typedArray = new Int32Array(binaryData.buffer);
        bytesPerElement = 4;
        break;
      case "uint":
      case "uint32":
        typedArray = new Uint32Array(binaryData.buffer);
        bytesPerElement = 4;
        break;
      case "uchar":
      case "unsigned char":
      case "uint8":
        typedArray = new Uint8Array(binaryData.buffer);
        bytesPerElement = 1;
        break;
      case "char":
      case "int8":
        typedArray = new Int8Array(binaryData.buffer);
        bytesPerElement = 1;
        break;
      default:
        throw new Error(`Unsupported NRRD data type: ${type}`);
    }

    return typedArray;
  }
}

async function loadNrrdNerves(app) {
  try {
    console.log("loadNrrdNerves");
    for (const nerve of vueApp.nerves) {
      await loadNrrdModel(app, nerve);
    }
  } catch (error) {
    console.error("Error loadNrrdNerves:", error);
  }
}

async function loadNrrdBones(app) {
  try {
    console.log("loadNrrdBones");
    for (const bone of vueApp.bones) {
      await loadNrrdModel(app, bone);
    }

    //annotation.allFigures[87][0].annotationPoints.get("147,40") == annotation.allAnnotationPoints[0]
  } catch (error) {
    console.error("Error loadNrrdBones:", error);
  }
}

async function loadNrrdModel(app, model) {
  const stlName = model.filename; //`${modelName}.stl`;  //`${modelName}.nrrd`;
  const nrrdName = model.filename.replace(".stl", ".nrrd");

  const annotation = app
    ? app.allAnnotations.find((x) => x.filename == stlName)
    : null;
  if (!annotation || annotation.isInitiliased) return;

  initAppNrrdMeta(app);

  const response = await fetch(`${vueApp.stlBaseUrl}/nrrd_bones/${nrrdName}`, {
    cache: "no-store",
  });
  if (response.status != 200) {
    return;
  }

  const arrayBuffer = await response.arrayBuffer();
  const uint8Array = new Uint8Array(arrayBuffer);

  // Find the header end (first empty line)
  let headerEnd = 0;
  let prevChar = null;
  for (let i = 0; i < uint8Array.length; i++) {
    if (uint8Array[i] === 0x0a && prevChar === 0x0a) {
      // \n\n
      headerEnd = i;
      break;
    }
    prevChar = uint8Array[i];
  }

  // Extract and parse header
  const headerText = new TextDecoder().decode(uint8Array.slice(0, headerEnd));
  const header = NRRDParser.parseNRRD(headerText).header;

  // Extract binary data
  const dataStart = headerEnd + 1;
  const binaryData = uint8Array.slice(dataStart);

  // Decode data based on encoding
  // const { typedArray, sizes } = NRRDParser.decodeData(header, binaryData);
  const typedArray = NRRDParser.decodeData(header, binaryData);

  let spX = app.nrrdMeta.spX;
  let spY = app.nrrdMeta.spY;
  let spZ = app.nrrdMeta.spZ;
  let originX = app.nrrdMeta.originX;
  let originY = app.nrrdMeta.originY;
  let originZ = app.nrrdMeta.originZ;

  let width = app.nrrdMeta.width;
  let height = app.nrrdMeta.height;
  let depth = app.nrrdMeta.depth;
  let sliceSize = app.nrrdMeta.sliceSize;

  const sizes = header["sizes"].split(" ").map(Number);
  let size_x = sizes[0];
  let size_y = sizes[1];
  let size_z = sizes[2];

  width = size_x;
  height = size_y;
  sliceSize = size_x * size_y;
  depth = size_z;

  let spaceDirections = header["space directions"]
    .replaceAll("(", "")
    .replaceAll(")", "")
    .split(" ");

  let spDirX = spaceDirections[0].split(",").map(Number)[0];
  let spDirY = spaceDirections[1].split(",").map(Number)[1];
  let spDirZ = spaceDirections[2].split(",").map(Number)[2];

  const spaceOrigins = header["space origin"]
    .replace("(", "")
    .replace(")", "")
    .split(",")
    .map(Number);
  let spOriginX = spaceOrigins[0]; // = 1;
  let spOriginY = spaceOrigins[1];
  let spOriginZ = spaceOrigins[2];

  let space = header["space"];

  if (space === "RAS") {
    spDirX = -spDirX;
    spDirY = -spDirY;
    spOriginX = -spOriginX;
    spOriginY = -spOriginY;
  }

  let min_x = (spOriginX - originX) / spDirX;
  let min_y = (spOriginY - originY) / spDirY;
  let relSizeZ = Math.abs(spDirZ) * size_z; // originZ + relSizeZ
  let min_z =
    spDirZ > 0
      ? (spOriginZ - originZ) / spDirZ
      : (spOriginZ - originZ - relSizeZ) / -spDirZ;
  let max_z = min_z + size_z;
  //(app.nrrdMeta.depth + depth*spDirZ);

  const startIndex = typedArray.indexOf(1);
  // typedArray.findIndex((x) => x > 0.1); //typedArray.indexOf(1);
  let pointOffset = startIndex; //typedArray.indexOf(1);
  const allPoints = [];
  annotation.allFigures = {};

  if (app.childApps) {
    app.childApps.forEach((capp) => {
      let item = structuredClone(annotation);
      item.appIndex = capp.index;
      item.parentAnnotation = annotation;
      if (!annotation.childAnnotations) {
        annotation.childAnnotations = [];
      }
      annotation.childAnnotations.push(item);
      capp.annotations.push(item);
    });
  }

  let relSpX = Math.abs(spDirX / spX);
  let relSpY = Math.abs(spDirY / spY);
  let relSpZ = Math.abs(spDirZ / spZ);
  let maxRel = Math.floor(Math.max(relSpX, relSpY, relSpZ));

  // { x: 1, y: 0, z: 0 }, { x: 0, y: 1, z: 0 }, { x: 1, y: 1, z: 0 }
  // { x: 0, y: 0, z: 1 }, { x: 1, y: 0, z: 1 }, { x: 0, y: 1, z: 1 }, { x: 1, y: 1, z: 1 }
  const neighborDirs = [];
  if (maxRel > 0) {
    for (let i = 0; i <= maxRel; i++) {
      for (let j = 0; j <= maxRel; j++) {
        for (let k = 0; k <= maxRel; k++) {
          if (i + j + k > 0) neighborDirs.push({ x: i, y: j, z: k });
        }
      }
    }
  }

  while (pointOffset >= 0) {
    let apoint = { x: 0, y: 0, z: 0 };
    apoint.z = Math.floor(pointOffset / sliceSize);
    if (apoint.z < 0) {
      console.log("");
    }
    let rowOffset = pointOffset - apoint.z * sliceSize;
    apoint.y = Math.floor(rowOffset / width);
    apoint.x = rowOffset % width;

    apoint.x += min_x;
    apoint.y += min_y;
    apoint.z = spDirZ > 0 ? apoint.z + min_z : max_z - apoint.z;

    apoint.x = Math.round(apoint.x * relSpX);
    apoint.y = Math.round(apoint.y * relSpY);
    apoint.z = Math.round(apoint.z * relSpZ);

    addPointInAnnotation(apoint, annotation, allPoints);

    for (const dir of neighborDirs) {
      let cpoint = {
        x: apoint.x + dir.x,
        y: apoint.y + dir.y,
        z: apoint.z + dir.z,
      };
      addPointInAnnotation(cpoint, annotation, allPoints);
    }

    pointOffset = typedArray.indexOf(1, pointOffset + 1);
    //  typedArray.findIndex((x, idx) => {
    //   return x > 0.1 && idx > pointOffset;
    // });

    //typedArray.indexOf(1, pointOffset + 1);
    if (pointOffset == startIndex) break;
  }

  // console.log("nrrd header", header, sizes, typedArray);

  annotation.allAnnotationPoints = allPoints;
  if (!app.annotations.includes(annotation)) {
    app.annotations.push(annotation);
  }

  annotation.isInitiliased = true;
}

function addPointInAnnotation(point, annotation, allPoints) {
  let apoint = point;
  if (annotation.appIndex == 2) {
    apoint = { x: point.x, y: point.z, z: point.y };
  }
  let sliceIndex = apoint.z + 1;
  if (!annotation.allFigures[sliceIndex]) {
    annotation.allFigures[sliceIndex] = [];
    let newFigure = {
      color: annotation.opacityColor,
      konvaPoints: [],
      annotationPoints: new Map(),
      changed: true,
      anchors: [],
      annotation: annotation,
    };
    annotation.allFigures[sliceIndex].push(newFigure);
  }
  let figure = annotation.allFigures[sliceIndex][0];
  const str = `${apoint.x},${apoint.y}`;
  if (!figure.annotationPoints.has(str)) {
    figure.annotationPoints.set(str, apoint);
    allPoints.push(apoint);
  }
}

function initAppNrrdMeta(app) {
  if (app && !app.nrrdMeta) {
    // https://ivmartel.github.io/dwv/doc/stable/Geometry.html

    if (!app.geometry) {
      const layerGroup = app.getLayerGroupByDivId(app.divId);
      const activeLayer = layerGroup.getActiveViewLayer();
      const viewController = activeLayer.getViewController();
      app.geometry = viewController.getViewImage().getGeometry();
    }
    const geometry = app.geometry;
    //app.getImage(0).getGeometry(); // getLastImage getImage(0)
    app.nrrdMeta = {};

    const size = geometry.getSize().getValues();
    let sizeX = (app.nrrdMeta.sizeX = +size[0].toFixed(3));
    let sizeY = (app.nrrdMeta.sizeY = +size[1].toFixed(3));
    let sizeZ = (app.nrrdMeta.sizeZ = +size[2].toFixed(3));

    const spacing = geometry.getSpacing().getValues(); // getRealSpacing getSpacing
    let spX = (app.nrrdMeta.spX = +spacing[0].toFixed(3));
    let spY = (app.nrrdMeta.spY = +spacing[1].toFixed(3));
    let spZ = (app.nrrdMeta.spZ = +spacing[2].toFixed(3));

    const origin = geometry.getOrigin();
    let originX = (app.nrrdMeta.originX = +origin.getX().toFixed(3));
    let originY = (app.nrrdMeta.originY = +origin.getY().toFixed(3));
    let originZ = (app.nrrdMeta.originZ = +origin.getZ().toFixed(3));

    let header = {};
    header["type"] = "unsigned char";
    header["dimension"] = "3";
    header["space"] = "3D-right-handed"; // ?
    header["sizes"] = `${sizeX} ${sizeY} ${sizeZ}`;
    header["space directions"] = `(${spX},0,0) (0,${spY},0) (0,0,${spZ})`;

    header["centerings"] = "cell cell cell";
    header["kinds"] = "space space space";
    header["encoding"] = "gzip"; //"raw"; "gzip";
    header["space origin"] = `(${originX},${originY},${originZ})`;
    header["measurement frame"] = "(1,0,0) (0,1,0) (0,0,1)"; // ?

    app.nrrdMeta.header = header;
    app.nrrdMeta.headerArray = convertNrrdHeaderToArray(header);

    app.nrrdMeta.width = sizeX;
    app.nrrdMeta.height = sizeY;
    app.nrrdMeta.depth = sizeZ;
    app.nrrdMeta.sliceSize = sizeX * sizeY;
    console.log("initAppNrrdMeta", app.nrrdMeta);
  }
}

function getAnnotationNrrdBlob(app, annotation) {
  let blob = null;
  if (app && annotation) {
    initAppNrrdMeta(app);

    let spX = app.nrrdMeta.spX;
    let spY = app.nrrdMeta.spY;
    let spZ = app.nrrdMeta.spZ;
    let originX = app.nrrdMeta.originX;
    let originY = app.nrrdMeta.originY;
    let originZ = app.nrrdMeta.originZ;

    let width = app.nrrdMeta.width;
    let height = app.nrrdMeta.height;
    let depth = app.nrrdMeta.depth;
    let sliceSize = app.nrrdMeta.sliceSize;
    let headerArray = app.nrrdMeta.headerArray;
    let header = app.nrrdMeta.header;

    let all_x = annotation.allAnnotationPoints.map((p) => p.x);
    let all_y = annotation.allAnnotationPoints.map((p) => p.y);
    let all_z = annotation.allAnnotationPoints.map((p) => p.z);

    let res_x = minMaxFromArray(all_x);
    let min_x = res_x.min;
    let max_x = res_x.max;
    let size_x = max_x - min_x + 1;

    let res_y = minMaxFromArray(all_y);
    let min_y = res_y.min;
    let max_y = res_y.max;
    let size_y = max_y - min_y + 1;

    let res_z = minMaxFromArray(all_z);
    let min_z = res_z.min;
    let max_z = res_z.max;
    let size_z = max_z - min_z + 1;

    header["space origin"] = `(${originX + min_x * spX},${
      originY + min_y * spY
    },${originZ + min_z * spZ})`;
    header["sizes"] = `${size_x} ${size_y} ${size_z}`;

    width = size_x;
    height = size_y;
    sliceSize = size_x * size_y;
    depth = size_z;

    headerArray = convertNrrdHeaderToArray(header);

    let newData = new Uint8Array(sliceSize * depth);
    for (const apoint of annotation.allAnnotationPoints) {
      let sliceOffset = (apoint.z - min_z) * sliceSize;
      let row = apoint.y - min_y;
      let column = apoint.x - min_x;
      let rowOffset = sliceOffset + row * width;
      let pointOffset = rowOffset + column;
      newData[pointOffset] = 1;
    }

    let newGzipData = pako.gzip(newData);
    let newBuffer = mergeTypedArrays(headerArray, newGzipData, true);
    blob = new Blob([newBuffer]);
  }
  return blob;
}

function saveAnnotationNrrd(app, annotation) {
  if (app && annotation) {
    const blob = getAnnotationNrrdBlob(app, annotation);
    if (blob) saveBlob(blob, `${annotation.name}.nrrd`);
  }
}

//#region Help functions

function convertNrrdHeaderToArray(header) {
  let headerText = "NRRD0005\n# Generated at ORCA Dental AI Viewer\n";
  for (const key in header) {
    if (Object.hasOwnProperty.call(header, key)) {
      const value = header[key];
      headerText += `${key}: ${value}\n`;
    }
  }
  const encoder = new TextEncoder();
  const headerArray = encoder.encode(headerText);

  return headerArray;
}

function mergeTypedArrays(a, b, withLF) {
  if (!a && !b) throw "Please specify valid arguments for parameters a and b.";

  if (!b || b.length === 0) return a;
  if (!a || a.length === 0) return b;

  if (Object.prototype.toString.call(a) !== Object.prototype.toString.call(b))
    throw "The types of the two arguments passed for parameters a and b do not match.";

  let length = a.length + b.length;
  if (withLF) length++;
  let c = new a.constructor(length);
  c.set(a);
  if (withLF) c[a.length] = 0x0a;
  c.set(b, withLF ? a.length + 1 : a.length);

  return c;
}

function minMaxFromArray(arr) {
  let len = arr.length;
  let max = -Infinity;
  let min = Infinity;

  while (len--) {
    let current = arr[len];
    if (current > max) max = current;
    if (current < min) min = current;
  }
  return { max: max, min: min };
}

//#endregion

export { loadNrrdBones, loadNrrdNerves };
